/**
 * Shortest gap (minutes) between Calendar Fixed BYDAY triggers in one RRULE week.
 * Frequency Test Matrix RC13 / Calendar Assum #4: overdue may equal this gap, not exceed it.
 *
 * Weekly Tue+Thu → 2 days (48h) shortest; Thu→Tue wrap is 5 days.
 * Single BYDAY / no BYDAY → falls back to FREQ×INTERVAL cadence (caller supplies).
 */

const MINUTES_PER_DAY = 1440

const BYDAY_OFFSET: Record<string, number> = {
  SU: 0,
  MO: 1,
  TU: 2,
  WE: 3,
  TH: 4,
  FR: 5,
  SA: 6,
}

/** Parse BYDAY codes from an RRULE string (strips optional ordinal like `-1FR`). */
export const parseRruleBydayOffsets = (frequencyRrule: string): number[] => {
  const bydayMatch = frequencyRrule.match(/BYDAY=([^;]+)/i)
  if (!bydayMatch) return []

  return bydayMatch[1]
    .split(',')
    .map((token) => BYDAY_OFFSET[token.replace(/^-?\d+/, '').toUpperCase()])
    .filter((offset): offset is number => offset != null)
    .sort((left, right) => left - right)
}

/**
 * Shortest whole-day gap between selected weekdays, in minutes.
 * Returns null when fewer than two distinct BYDAY values (no multi-day gap to enforce).
 */
export const shortestBydayGapMinutes = (frequencyRrule: string): number | null => {
  const dayOffsets = [...new Set(parseRruleBydayOffsets(frequencyRrule))]
  if (dayOffsets.length < 2) return null

  const gaps: number[] = []
  for (let index = 0; index < dayOffsets.length - 1; index += 1) {
    gaps.push(dayOffsets[index + 1] - dayOffsets[index])
  }
  gaps.push(dayOffsets[0] + 7 - dayOffsets[dayOffsets.length - 1])

  const shortestDays = Math.min(...gaps)
  const intervalMatch = frequencyRrule.match(/INTERVAL=(\d+)/i)
  const rruleInterval = intervalMatch ? parseInt(intervalMatch[1], 10) : 1
  return shortestDays * MINUTES_PER_DAY * rruleInterval
}

/**
 * Matrix RC14: shortest monthly trigger gap in minutes.
 * Clamped month-end schedules (31st) bottom out at non-leap February → 28 days.
 * Returns null when FREQ is not MONTHLY.
 */
export const shortestMonthlyGapMinutes = (frequencyRrule: string): number | null => {
  if (!/FREQ=MONTHLY/i.test(frequencyRrule)) return null

  const intervalMatch = frequencyRrule.match(/INTERVAL=(\d+)/i)
  const rruleInterval = intervalMatch ? parseInt(intervalMatch[1], 10) : 1
  return 28 * MINUTES_PER_DAY * rruleInterval
}
